import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalBankIdDebug } from '../context/LocalBankIdDebugContext';
import { orgNumberApi } from '../services/api';
import { environmentForHost, testModeAccess } from '../utils/appEnvironment';

export function TestOrgNumberButton({ onGenerated, disabled = false }: {
  onGenerated: (organizationNumber: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const debug = useLocalBankIdDebug();
  const environment = debug.enabled && debug.environment === 'testserver'
    ? 'testserver' : environmentForHost(window.location.hostname);
  const allowed = testModeAccess(environment, debug.enabled) !== 'locked' && !disabled;
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const pending = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    let active = true;
    const current = generation.current;
    setEnabled(false);
    setBusy(false);
    setFailed(false);
    if (allowed) {
      const load = async () => {
        try {
          const result = await orgNumberApi.testCapabilities();
          if (active) setEnabled(result.enabled === true);
        } catch { /* An unavailable API keeps the debug action hidden. */ }
      };
      void load();
    }
    return () => { active = false; generation.current = current + 1; pending.current = false; };
  }, [allowed]);

  const generate = async () => {
    if (pending.current || !allowed || !enabled) return;
    pending.current = true;
    const current = generation.current;
    setBusy(true);
    setFailed(false);
    try {
      const result = await orgNumberApi.generateTestNumber();
      if (current === generation.current) onGenerated(result.organizationNumber);
    } catch {
      if (current === generation.current) setFailed(true);
    } finally {
      if (current === generation.current) { pending.current = false; setBusy(false); }
    }
  };

  if (!allowed || !enabled) return null;
  return <div className="mt-2">
    <button type="button" className="btn btn-outline-secondary btn-lg w-100 text-wrap"
      disabled={busy} onClick={() => void generate()}>
      {t(busy ? 'testOrgNumber.generating' : 'testOrgNumber.generate')}
    </button>
    <div className="form-text">{t('testOrgNumber.help')}</div>
    {failed && <div className="text-danger mt-1" role="alert">{t('testOrgNumber.failed')}</div>}
  </div>;
}
